import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNotification } from "../../contexts/NotificationContext";
import { api } from "../../services/api";

export default function DownloadChecksum({
  versionId,
  resourceId,
  initiallyExpanded = false,
  initialMetadata = null,
}) {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [metadata, setMetadata] = useState(initialMetadata);
  const [error, setError] = useState("");
  const { showNotification } = useNotification();
  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    let timer;
    async function load() {
      try {
        const { data } = await api.get(
          resourceId != null
            ? `/resources/${resourceId}/metadata`
            : `/downloads/metadata/${versionId}`,
        );
        if (cancelled) return;
        setError("");
        setMetadata(data);
        if (data.checksum_status === "pending") timer = setTimeout(load, 10000);
      } catch (err) {
        if (!cancelled)
          setError(err.response?.data?.message || "Could not load checksum.");
      }
    }
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [expanded, versionId, resourceId]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(metadata.sha256);
      showNotification("SHA-256 checksum copied", "success");
    } catch {
      showNotification("Could not copy checksum", "error");
    }
  };
  if (error || metadata?.checksum_status !== "ready" || !metadata.sha256)
    return null;
  return (
    <Accordion
      expanded={expanded}
      onChange={(_, value) => setExpanded(value)}
      sx={{ mt: 2, boxShadow: "none", border: 1, borderColor: "divider" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>Verify download — SHA-256 Checksum</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2">
          {metadata.file_name || metadata.file_path} ·{" "}
          {(Number(metadata.file_size) / 1024 ** 3).toFixed(2)} GB
        </Typography>
        <Typography
          component="code"
          sx={{
            display: "block",
            overflowWrap: "anywhere",
            fontFamily: "monospace",
            my: 1,
          }}
        >
          {metadata.sha256}
        </Typography>
        <Button onClick={copy}>Copy SHA-256 Hash</Button>
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="body2">
            After downloading, calculate the file’s SHA-256 hash locally and
            compare all 64 characters above, ignoring letter case. Matching
            values confirm your file matches the server’s file.
          </Typography>
          <Typography variant="subtitle2">Windows PowerShell</Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              fontSize: "0.8rem",
            }}
          >{String.raw`Get-FileHash -LiteralPath "C:\Downloads\your-file.zip" -Algorithm SHA256`}</Box>
          <Typography variant="subtitle2">macOS / Linux</Typography>
          <Box
            component="pre"
            sx={{ m: 0, whiteSpace: "pre-wrap", fontSize: "0.8rem" }}
          >
            {'shasum -a 256 "your-file.zip"'}
          </Box>
          <Typography variant="caption">
            Replace the example path with your downloaded file. Verification
            reads the file locally; it does not upload it.
          </Typography>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
